import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, Loader2 } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import ProductCard from "../components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { SEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

export default function Shop() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const selectedBrand = searchParams.get("brand") || "";
  const selectedCategory = searchParams.get("category") || "";
  const searchQuery = searchParams.get("search") || "";
  const [priceRange, setPriceRange] = useState([0, 15000]);
  const [sort, setSort] = useState("popular");

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [bRes, cRes] = await Promise.all([
          axios.get(`${API}/brands`),
          axios.get(`${API}/categories`),
        ]);
        setBrands(bRes.data.map((b) => (typeof b === "string" ? b : b.name)));
        setCategories(cRes.data.map((c) => (typeof c === "string" ? c : c.name)));
      } catch (e) {
        console.error(e);
      }
    };
    loadMeta();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
    setProducts([]);
  }, [selectedBrand, selectedCategory, priceRange, searchQuery, sort]);

  const loadProducts = useCallback(async (pageNum, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = { page: pageNum, limit: PAGE_SIZE, sort };
      if (selectedBrand) params.brand = selectedBrand;
      if (selectedCategory) params.category = selectedCategory;
      if (priceRange[0] > 0) params.min_price = priceRange[0];
      if (priceRange[1] < 15000) params.max_price = priceRange[1];
      if (searchQuery) params.search = searchQuery;

      const res = await axios.get(`${API}/products`, { params });
      const data = res.data;
      if (append) {
        setProducts((prev) => [...prev, ...data.products]);
      } else {
        setProducts(data.products);
      }
      setTotal(data.total);
      setHasMore(data.has_more);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedBrand, selectedCategory, priceRange, searchQuery, sort]);

  useEffect(() => {
    loadProducts(page, page > 1);
  }, [page, loadProducts]);

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price_asc") arr.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") arr.sort((a, b) => b.price - a.price);
    return arr;
  }, [products, sort]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const clearFilters = () => {
    setSearchParams({});
    setPriceRange([0, 15000]);
    setPage(1);
    setProducts([]);
  };

  const hasFilters = selectedBrand || selectedCategory || searchQuery || priceRange[0] > 0 || priceRange[1] < 15000;

  return (
    <div className="px-6 md:px-12 lg:px-24 py-12 md:py-16" data-testid="shop-page">
      <SEO
        title={selectedBrand ? `${selectedBrand} Handbags` : "Shop Pre-Owned Designer Handbags"}
        description={selectedBrand
          ? `Shop authentic pre-owned ${selectedBrand} handbags at Mona Luxe. Verified authenticity, 14-day returns.`
          : "Browse our curated collection of authentic pre-owned luxury designer handbags. Chanel, Louis Vuitton, Hermès, Gucci and more."
        }
        path="/shop"
      />
      {/* Header */}
      <div className="flex items-end justify-between mb-12">
        <div>
          <h1
            className="text-3xl md:text-4xl mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="shop-title"
          >
            {searchQuery ? `"${searchQuery}"` : t("shop.title")}
          </h1>
          <p className="text-xs text-[#9CA3AF] tracking-wide">
            {total} {t("shop.results")}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 text-xs uppercase tracking-widest"
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal size={16} strokeWidth={1.5} />
            {t("shop.filters")}
          </button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger
              className="w-[180px] text-xs tracking-wide border-[#E6E2DD] bg-transparent"
              data-testid="sort-select"
            >
              <SelectValue placeholder={t("shop.sort")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="popular" data-testid="sort-popular">{t("shop.sort_popular")}</SelectItem>
              <SelectItem value="newest" data-testid="sort-newest">{t("shop.sort_newest")}</SelectItem>
              <SelectItem value="price_asc" data-testid="sort-price-asc">{t("shop.sort_price_asc")}</SelectItem>
              <SelectItem value="price_desc" data-testid="sort-price-desc">{t("shop.sort_price_desc")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-12">
        {/* Filters Sidebar */}
        <aside
          className={`${
            showFilters ? "block" : "hidden"
          } md:block w-full md:w-56 lg:w-64 flex-shrink-0`}
          data-testid="filters-sidebar"
        >
          <div className="sticky top-24 space-y-10">
            {/* Brands */}
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.15em] text-[#9CA3AF] mb-4">
                {t("shop.brand")}
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => setFilter("brand", "")}
                  className={`block text-xs tracking-wide transition-opacity ${
                    !selectedBrand ? "opacity-100 font-bold" : "opacity-50 hover:opacity-100"
                  }`}
                  data-testid="filter-brand-all"
                >
                  {t("shop.all")}
                </button>
                {brands.map((b) => (
                  <button
                    key={b}
                    onClick={() => setFilter("brand", b)}
                    className={`block text-xs tracking-wide transition-opacity ${
                      selectedBrand === b ? "opacity-100 font-bold" : "opacity-50 hover:opacity-100"
                    }`}
                    data-testid={`filter-brand-${b.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.15em] text-[#9CA3AF] mb-4">
                {t("shop.category")}
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => setFilter("category", "")}
                  className={`block text-xs tracking-wide transition-opacity ${
                    !selectedCategory ? "opacity-100 font-bold" : "opacity-50 hover:opacity-100"
                  }`}
                  data-testid="filter-category-all"
                >
                  {t("shop.all")}
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setFilter("category", c)}
                    className={`block text-xs tracking-wide transition-opacity ${
                      selectedCategory === c ? "opacity-100 font-bold" : "opacity-50 hover:opacity-100"
                    }`}
                    data-testid={`filter-category-${c.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.15em] text-[#9CA3AF] mb-4">
                {t("shop.price")}
              </h3>
              <Slider
                value={priceRange}
                onValueChange={setPriceRange}
                min={0}
                max={15000}
                step={100}
                className="mb-4"
                data-testid="price-range-slider"
              />
              <div className="flex justify-between text-[10px] text-[#9CA3AF] tracking-wider">
                <span>&euro;{priceRange[0].toLocaleString()}</span>
                <span>&euro;{priceRange[1].toLocaleString()}</span>
              </div>
            </div>

            {/* Clear */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 text-xs tracking-wide text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors"
                data-testid="clear-filters-btn"
              >
                <X size={14} strokeWidth={1.5} />
                {t("shop.clear")}
              </button>
            )}
          </div>
        </aside>

        {/* Product Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-[#F4F0EB]" />
                  <div className="mt-4 space-y-2">
                    <div className="h-2 bg-[#F4F0EB] w-16" />
                    <div className="h-3 bg-[#F4F0EB] w-32" />
                    <div className="h-2 bg-[#F4F0EB] w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-24" data-testid="no-results">
              <p className="text-sm text-[#9CA3AF] tracking-wide">{t("shop.no_results")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="products-grid">
              {sorted.map((product, i) => (
                <ProductCard key={product.id} product={product} index={i} />
              ))}
            </div>
          )}

          {/* Load More */}
          {hasMore && !loading && (
            <div className="flex justify-center mt-12">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="btn-luxury-outline inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] px-8 py-3 disabled:opacity-50"
                data-testid="load-more-btn"
              >
                {loadingMore ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {t("shop.loading")}
                  </>
                ) : (
                  t("shop.load_more")
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
